import type { BulkPropertyRow, ReuseCandidate } from "../types";
import type { AcquirePolicy } from "./acquirePolicy";
import { CHECKBOX_FIELDS, isAllMattersField } from "./checkboxFields";
import { isWithinWindow, zenbujikoFields } from "./reuseAvailability";
import { splitCandidates } from "./reuseMarks";

// 取得済みは自社取得で無償、リユースは他社取得で有償と扱いが違うため区別して見せる
const POOL_LABEL = { acquired: "取得済み", reusable: "リユース" } as const;

/**
 * 全部事項の構成表示（共担+信託 など。列の並びは CHECKBOX_FIELDS に合わせる）。
 * 区切りを「+」にするのは、この構成をまとめてチェックしたときだけリユースできることを示すため
 */
const formatZenbujikoOptions = (candidate: ReuseCandidate): string => {
  const fields = zenbujikoFields(candidate);
  return CHECKBOX_FIELDS.filter(({ field }) => fields.includes(field))
    .map(({ headerName }) => headerName)
    .join("+");
};

/**
 * チェックボックスに表示する候補の一覧。取得済みを先に並べ、自身の明細はリユース側に出さない
 * （splitCandidates が除く）。目録なしの全部事項は一括取得では請求できないため、
 * 列を含まない候補と同じく分割の時点で外れる。経過日数の範囲外の候補も、対象外と分かる形で残す
 * （なぜチェックできないのかを候補の取得日から判断できるようにするため）。
 * 全部事項は「構成 取得日」、それ以外は取得日のみを出す。
 * 未取得のみを取得する指定のときは、取得済み候補は指定を外さない限り使えないため注釈を付けない
 * （経過日数の注釈があると、経過日数を延ばせば選べると誤読されるため）
 *
 * @example
 * buildReuseTooltipLines("kyoutan", row, { steps: ["acquired", "reuse"], ignoreWindow: false }, 30, now)
 * // [
 * //   "取得済み: 共担+信託 2026-06-14",
 * //   "リユース: 共担 2026-06-09（経過日数の範囲外のためリユース登記は取得されません）",
 * // ]
 */
export const buildReuseTooltipLines = (
  field: string,
  row: BulkPropertyRow,
  policy: AcquirePolicy,
  pastDays: number,
  now: Date,
  excludeAcquired = false
): string[] => {
  const isAllMatters = isAllMattersField(field);
  // 取得済みと他ユーザーのリユースに分けた候補（印・不可判定と同じ分割を使う）
  const split = splitCandidates(field, row);
  if (split == null) {
    return [];
  }

  // 今の条件で使えない候補も、取得方法を変えれば使えると判断できるよう理由付きで出す
  const STEP_BY_POOL = { acquired: "acquired", reusable: "reuse" } as const;
  const reason = (
    pool: "acquired" | "reusable",
    candidate: ReuseCandidate
  ): string => {
    if (pool === "acquired" && excludeAcquired) {
      return "";
    }
    if (!policy.steps.includes(STEP_BY_POOL[pool])) {
      return `（現在の取得方法では${POOL_LABEL[pool]}登記は取得されません）`;
    }
    // 時間窓を使わない条件では、取得日の有無・古さで対象外にならない
    if (policy.ignoreWindow) {
      return "";
    }
    if (candidate.latestReceivedDate == null) {
      return "（取得日が未設定のため対象外）";
    }
    if (!isWithinWindow(candidate, pastDays, now)) {
      return `（経過日数の範囲外のため${POOL_LABEL[pool]}登記は取得されません）`;
    }
    return "";
  };

  const lines: string[] = [];
  for (const pool of ["acquired", "reusable"] as const) {
    for (const candidate of split[pool]) {
      const options = isAllMatters ? formatZenbujikoOptions(candidate) : "";
      lines.push(
        `${POOL_LABEL[pool]}: ${options === "" ? "" : `${options} `}${
          candidate.latestReceivedDate ?? "取得日なし"
        }${reason(pool, candidate)}`
      );
    }
  }
  return lines;
};
